import { Button, Modal, Textarea, Stack, Alert, Group, LoadingOverlay } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconInfoCircle } from '@tabler/icons-react'
import { useState } from 'react'
import { logError } from '@/utils/errorUtils'
import { useMedplum } from '@medplum/react'
import type { Parameters } from '@medplum/fhirtypes'

interface ConsentFormData {
  template: string
}

export function PatientAppointmentConsentModal() {
  const [opens, setOpens] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [existingParameterId, setExistingParameterId] = useState<string | undefined>()
  const medplum = useMedplum()

  const form = useForm<ConsentFormData>({
    initialValues: {
      template: '',
    },
    validate: {
      template: (value) => (!value?.trim() ? 'Consent template is required' : null),
    },
  })

  const toggleModal = () => {
    setOpens(!opens)
    if (!opens) {
      // Reset form when opening
      form.reset()
      loadExistingTemplate()
    }
  }

  const loadExistingTemplate = async () => {
    try {
      setIsLoadingData(true)
      const searchResult = await medplum.searchResources('Parameters', {
        _source: 'system-patient-appointment-consent',
      })

      if (searchResult.length > 0) {
        const existingParameter = searchResult[0]
        const templateParameter = existingParameter.parameter?.find((p) => p.name === 'template')

        setExistingParameterId(existingParameter.id)
        form.setValues({
          template: templateParameter?.valueString || '',
        })
      } else {
        setExistingParameterId(undefined)
        form.setValues({ template: '' })
      }
    } catch (error) {
      logError('super', 'Error loading existing consent template', error)
      notifications.show({
        title: 'Warning',
        message: 'Could not load existing consent template.',
        color: 'yellow',
      })
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleSubmit = async (values: ConsentFormData) => {
    try {
      setIsLoading(true)

      const parameter: Parameters = {
        resourceType: 'Parameters',
        meta: {
          source: 'system-patient-appointment-consent',
        },
        parameter: [
          {
            name: 'template',
            valueString: values.template,
          },
        ],
      }

      let result
      if (existingParameterId) {
        // Update existing
        parameter.id = existingParameterId
        result = await medplum.updateResource(parameter)
      } else {
        // Create new
        const fhirProject = await medplum.searchOne('Project', 'name=FHIR')
        if (fhirProject) {
          parameter.meta!.project = fhirProject.id
          parameter.meta!.compartment = [
            {
              reference: `Project/${fhirProject.id}`,
            },
          ]
        }
        result = await medplum.createResource(parameter)
        setExistingParameterId(result.id)
      }

      notifications.show({
        title: 'Success',
        message: `Consent template ${existingParameterId ? 'updated' : 'created'} successfully`,
        color: 'green',
      })

      toggleModal()
    } catch (error) {
      logError('super', 'Error saving consent template', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to save consent template. Please try again.',
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Button variant="subtle" leftSection={<IconEdit size={20} />} onClick={toggleModal}>
        Patient Appointment Consent
      </Button>
      <Modal
        opened={opens}
        onClose={toggleModal}
        title="Patient Appointment Consent Template"
        size="xl"
        centered
      >
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={isLoadingData} />

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Alert
                icon={<IconInfoCircle size="1rem" />}
                title="Available Template Variables"
                mb="md"
              >
                <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Organization variables:</strong>
                  <br />• {`{{organization.name}}`} - Organization name
                  <br />
                  <br />
                  <strong>Patient variables:</strong>
                  <br />• {`{{patient.name}}`} - Patient's full name
                  <br />• {`{{patient.first_name}}`} - Legal first name
                  <br />• {`{{patient.middle_name}}`} - Legal middle name
                  <br />• {`{{patient.last_name}}`} - Legal last name
                  <br />• {`{{patient.dob}}`} - Date of birth (MM/DD/YYYY)
                  <br />• {`{{patient.phone}}`} - Phone number
                  <br />• {`{{patient.email}}`} - Email address
                  <br />• {`{{patient.address}}`} - Home address on one line
                  <br />• {`{{patient.address_line}}`} - Street address only
                  <br />• {`{{patient.city}}`} - City
                  <br />• {`{{patient.state}}`} - State
                  <br />• {`{{patient.address_state}}`} - State (same as above)
                  <br />• {`{{patient.zip}}`} - ZIP code
                  <br />
                  <br />A variable with no value on the patient record renders as blank and never
                  blocks signing. A misspelled variable is left in the document as-is, so proofread
                  the generated consent after saving.
                </div>
              </Alert>

              <Textarea
                label="Consent Template"
                description="Enter the consent template content. Use the template variables shown above to personalize the consent form."
                placeholder="Enter your consent template here..."
                {...form.getInputProps('template')}
                rows={15}
                required
              />

              <Group justify="flex-end" mt="md">
                <Button variant="outline" onClick={toggleModal} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" loading={isLoading}>
                  {existingParameterId ? 'Update Template' : 'Create Template'}
                </Button>
              </Group>
            </Stack>
          </form>
        </div>
      </Modal>
    </>
  )
}

/*
{{organization.name}}

{{patient.name}}

{{patient.address_state}}
 */

/*
{{organization.name}} – Master Consent & Acknowledgment

1. Consent for Treatment
By agreeing below, I, {{patient.name}}, consent to receive medical evaluation, diagnosis, treatment, and follow-up care from clinicians affiliated with {{organization.name}}, including physicians, nurse practitioners, and other licensed professionals.
This includes ordering and reviewing lab tests, imaging, prescriptions, and preventive screenings.
I understand that my care may be delivered by telehealth or in-person, depending on my needs.
I understand that medicine is not an exact science, and no guarantees have been made about results.

2. Telehealth Consent
I consent to the use of telehealth technologies (video, phone, or secure messaging) to receive care.
I understand that telehealth has limitations compared to in-person care, including possible technology interruptions.
I understand that my provider may recommend in-person care if it is clinically necessary.
I confirm that at the time of my telehealth visit, I will be physically located in the state of {{patient.address_state}}, where my provider is licensed.

3. HIPAA Acknowledgment & Privacy
I acknowledge that I have received or had the opportunity to review the Notice of Privacy Practices (NPP) describing how my health information may be used and shared under HIPAA https://www.behuman.health/privacy).
My information may be shared with payers, pharmacies, labs, and other providers for treatment, payment, or healthcare operations.
I understand that I may ask for restrictions on specific uses or disclosures.

4. Assignment of Benefits & Financial Responsibility
I authorize {{organization.name}} to bill my insurance directly for services I receive.
I assign insurance benefits to be paid directly to {{organization.name}}.
I understand that I am responsible for copayments, deductibles, or charges not covered by my plan.
If I do not have insurance or if my plan does not cover certain services, I agree to be financially responsible.

5. Communication Consent
I consent to receive appointment reminders, test results, and other communications by phone, email, or text message.
I understand that these methods may carry some risk of unauthorized access.
I may change my communication preferences at any time by letting the practice know.

6. Patient Rights & Responsibilities
I have the right to respectful, private, and timely care.
I agree to provide accurate health information and to engage honestly with my provider.
I understand that abusive or inappropriate behavior may result in dismissal from care.

7. Optional Program Enrollment (if applicable) For patients enrolled in ongoing care management
programs (such as Principal Care Management [PCM] or Chronic Care Management [CCM]):
I consent to monthly outreach, follow-up, and coordination by my care team.
I understand this program may be billed monthly to my insurance, and
I may not enroll with another provider for the same program at the same time.

8. Patient Authorization & Acknowledgment
By checking the box and selecting “I Agree” below, I confirm that:
• I have read and understood this Master Consent & Acknowledgment.
• I had the opportunity to ask questions.
• I consent to treatment and acknowledge HIPAA, telehealth, billing, and communication terms above.
• My electronic acknowledgment carries the same legal effect as a handwritten signature.

 */
